import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
     Button,  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    TablePagination, Checkbox,  Box, Typography
} from '@mui/material';
import {FilterList} from '@mui/icons-material';
import {deleteFournisseur} from '../services/projectService';
import '../App.css';
import { formatDateToDDMMYYYY } from '../utils/dateUtils';
import {Fournisseur} from "../interfaces/Fournisseur";

interface FournisseurTableProps {
    fournisseur: Fournisseur[];
    onEditFournisseur: (fournisseur: Fournisseur) => void;
    refreshFournisseurs: () => void;
}

const FournisseurTable: React.FC<FournisseurTableProps> = ({ fournisseur, onEditFournisseur, refreshFournisseurs }) => {
    const [filter, setFilter] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [fournisseurToDelete, setFournisseurToDelete] = useState<Fournisseur | null>(null);
    const [page, setPage] = useState(0); // Page actuelle
    const [rowsPerPage, setRowsPerPage] = useState(10); // Nombre de lignes par page
    const [selectedFournisseur, setSelectedFournisseur] = useState<number[]>([]); // Projets sélectionnés

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setFournisseurToDelete(null);
    };

    const confirmDelete = async () => {
        if (fournisseurToDelete) {
            try {
                await deleteFournisseur(fournisseurToDelete.id);
                refreshFournisseurs();
            } catch (error) {
                console.error('Erreur lors de la suppression du fournisseur:', error);
            } finally {
                closeDeleteModal();
            }
        }
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedFournisseur(filteredFournisseurs.map(provider => provider.id));
        } else {
            setSelectedFournisseur([]);
        }
    };

    const handleSelectFournisseur = (fournisseurId: number) => {
        setSelectedFournisseur(prevSelected =>
            prevSelected.includes(fournisseurId)
                ? prevSelected.filter(id => id !== fournisseurId)
                : [...prevSelected, fournisseurId]
        );
    };

    const filteredFournisseurs = fournisseur
        .filter(provider =>
            provider.libelleCommercial.toLowerCase().includes(filter.toLowerCase())
        );

    // Pagination logic
    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedProjects = filteredFournisseurs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper elevation={3} sx={{ p: 3, width: '100%', position: 'relative' }}>

            {/* Panneau de filtres */}
            <Box mb={3} p={2} sx={{ border: '2px solid #4caf50', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    <FilterList sx={{ mr: 1, color: '#4caf50' }} />
                    Filtres
                </Typography>

                <Box sx={{ columns: 3, mt: 2 }}>
                    <TextField
                        label="Libellé commercial du fournisseur"
                        variant="outlined"
                        fullWidth
                        style={{ marginTop: 2 }}
                        margin="normal"
                        value={filter}
                        onChange={handleFilterChange}
                    />


                </Box>
            </Box>

            {filteredFournisseurs.length > 0 ? (
                <Box>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                    >
                        {selectedFournisseur.length > 0 && (
                            <>
                            </>

                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                position: 'relative',
                                top: 8,
                                mb: 3,
                            }}
                            onClick={() => {
                                refreshFournisseurs()
                            }}
                        >
                            Rechercher
                        </Button>
                    </Box>

                    <TableContainer component={Paper} sx={{ border: '2px solid #2196f3' }}>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#2196f3' }}>
                                <TableRow>
                                    <TableCell padding="checkbox" >
                                        <Checkbox
                                            checked={selectedFournisseur.length === filteredFournisseurs.length}
                                            indeterminate={selectedFournisseur.length > 0 && selectedFournisseur.length < filteredFournisseurs.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>ID</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>Libellé Commercial</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>Réference</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>date Création</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>date Suppression</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedProjects.map((fournisseur) => (
                                    <TableRow
                                        key={fournisseur.id}
                                        sx={{
                                            backgroundColor: selectedFournisseur.includes(fournisseur.id) ? '#e3f2fd' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: selectedFournisseur.includes(fournisseur.id) ? '#e3f2fd' : '#f5f5f5', // Couleur de surbrillance
                                                cursor: 'pointer'
                                            }
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedFournisseur.includes(fournisseur.id)}
                                                onChange={() => handleSelectFournisseur(fournisseur.id)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">{fournisseur.id}</TableCell>
                                        <TableCell align="center">{fournisseur.libelleCommercial}</TableCell>
                                        <TableCell align="center">{fournisseur.reference}</TableCell>
                                        <TableCell align="center">{formatDateToDDMMYYYY(fournisseur.dateCreation)}</TableCell>
                                        <TableCell align="center">{formatDateToDDMMYYYY(fournisseur.dateSuppression)}</TableCell>
                                        <TableCell align="center">
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={filteredFournisseurs.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            ) : (
                <p>Aucun fournisseur trouvé.</p>
            )}

            {/* Modal de confirmation de suppression */}
            <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer le fournisseur "{fournisseurToDelete?.libelleCommercial}" ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteModal} color="primary">
                        Annuler
                    </Button>
                    <Button onClick={confirmDelete} color="error">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default FournisseurTable;